import { useMemo } from "react";
import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useAssistenciaTecnicaListaViewModel } from "../../viewModels/assistencia_tecnica_lista_view_model";
import { Header } from "./components/header";
import { AssistenciaTecnicaLista as AssistenciaTecnicaListaModal } from "../../../domain/models/assistencia_tecnica_lista";
import { GenerateAssistenciaTecnicaListaColumnDefinition } from "./columns";
import { GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { ReportAssistenciaTecnicaModal } from "./components/report_assistencia_tecnica_modal";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";

import PrimaryButton from "../../../../../common/components/primary_button";
import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";

/**
 * AssistenciaTecnicaLista
 */

export const AssistenciaTecnicaLista = () => {

  const viewModel = useAssistenciaTecnicaListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateAssistenciaTecnicaListaColumnDefinition({
      onActionEditar: viewModel.handleAssistenciaTecnicaEditar,
      onActionDelete: viewModel.handleAssistenciaTecnicaDeleteOpenState,
    });
  }, [
    viewModel.handleAssistenciaTecnicaEditar,
    viewModel.handleAssistenciaTecnicaDeleteOpenState,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields | undefined => {
    return viewModel.assistenciasTecnicas.length > 0
      ? { right: ['idAssistenciaTecnica'] }
      : {};
  }, [
    viewModel.assistenciasTecnicas,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  buttonColor="yellow"
                  onMouseDown={e => {
                    viewModel.handleAssistenciaTecnicaReportOpenState(true);
                    e.preventDefault();
                  }}>
                  Relatório
                </PrimaryButton>
              </div>

              <div className="w-[115px]">
                <PrimaryButton
                  buttonColor="primary-green"
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleAssistenciaTecnicaEditar(null, true);
                    } else {
                      viewModel.handleAssistenciaTecnicaEditar(null, e.ctrlKey);
                    }

                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Entrega de Obras
          </Link>
          <Typography className="text-primary-green">
            Assistência Técnica
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-0">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-2xl font-medium text-black dark:text-white">
                Assistência Técnica
              </h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-start flex gap-2">

            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  idCliente={viewModel.filters.idCliente}
                  cliente={viewModel.filters.cliente}
                  centroCustos={viewModel.filters.centroCustos}
                  local={viewModel.filters.local}
                  dataAberturaDe={viewModel.filters.dataAberturaDe}
                  dataAberturaAte={viewModel.filters.dataAberturaAte}
                  tipo={viewModel.filters.tipo}
                  dataEncerramentoDe={viewModel.filters.dataEncerramentoDe}
                  dataEncerramentoAte={viewModel.filters.dataEncerramentoAte}
                  codigo={viewModel.filters.codigo}
                  procedente={viewModel.filters.procedente}
                  categorias={viewModel.filters.categorias}
                  resolvido={viewModel.filters.resolvido}
                  prioridade={viewModel.filters.prioridade}
                  nroChamado={viewModel.filters.nroChamado}
                  status={viewModel.filters.status}
                  clientesDropdown={viewModel.clientesDropdown}
                  centroCustosDropdown={viewModel.centroCustosDropdown}
                  categoriasDropdown={viewModel.categoriasDropdown}
                  onIdClienteChange={viewModel.handleIdClienteChange}
                  onClienteChange={viewModel.handleClienteChange}
                  onCentroCustosChange={viewModel.handleCentroCustosChange}
                  onLocalChange={viewModel.handleLocalChange}
                  onDataAberturaChange={viewModel.handleDataAberturaChange}
                  onTipoChange={viewModel.handleTipoChange}
                  onDataEncerramentoChange={viewModel.handleDataEncerramentoChange}
                  onCodigoChange={viewModel.handleCodigoChange}
                  onProcedenteChange={viewModel.handleProcedenteChange}
                  onCategoriasChange={viewModel.handleCategoriasChange}
                  onResolvidoChange={viewModel.handleResolvidoChange}
                  onPrioridadeChange={viewModel.handlePrioridadeChange}
                  onNroChamadoChange={viewModel.handleNroChamadoChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="categoria-assistencia-lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    getRowId={(row: AssistenciaTecnicaListaModal) => row.idAssistenciaTecnica}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.assistenciasTecnicas}
                    rowCount={viewModel.assistenciasTecnicas.length}
                    pinnedColumns={pinnedColumns}
                    pageSizeOptions={[10, 25, 50, 100]}
                    textSize="muitoPequeno"
                    getRowHeight={() => 'auto'}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />
                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <ReportAssistenciaTecnicaModal
        open={viewModel.assistenciaTecnicaReportModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        clientesDropDown={viewModel.clientesDropdown}
        centroCustosDropDown={viewModel.centroCustosDropdown}
        categoriaDropDown={viewModel.categoriasDropdown}
        onClose={() => viewModel.handleAssistenciaTecnicaReportOpenState(false)}
        onSubmit={viewModel.handleAssistenciaTecnicaReportSubmit}
      />

      <DeleteDialog
        open={viewModel.assistenciaTecnicaDeleteModal}
        saving={viewModel.saving}
        title="Você realmente deseja excluir este item?"
        onClose={() => viewModel.handleAssistenciaTecnicaDeleteOpenState(null, false)}
        onDelete={() => viewModel.handleAssistenciaTecnicaDeleteSubmit()}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />
    </>
  );
}